import type { PeriodValue } from "./period-types";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
] as const;

/**
 * Converts a PeriodValue to the set of calendar months (0–11) it covers.
 * Returns [] for null (null entries contribute no coverage).
 */
export function periodToMonths(period: PeriodValue | null): number[] {
  if (!period) return [];

  switch (period.period) {
    case "":         return [0,1,2,3,4,5,6,7,8,9,10,11]; // annually
    case "H1":       return [0,1,2,3,4,5];
    case "H2":       return [6,7,8,9,10,11];
    case "Q1":       return [0,1,2];
    case "Q2":       return [3,4,5];
    case "Q3":       return [6,7,8];
    case "Q4":       return [9,10,11];
    default: {
      // Monthly period — look up month index by name
      const idx = MONTH_NAMES.indexOf(period.period as typeof MONTH_NAMES[number]);
      return idx === -1 ? [] : [idx];
    }
  }
}

/**
 * Returns the set of calendar months (0–11) the assessment window spans.
 * Uses getUTCMonth() — dates are stored as UTC midnight (inclusive end).
 * Returns null for cross-year windows (unsupported — vacuous pass in hasCoverage).
 */
export function getAssessmentMonths(periodStart: Date, periodEnd: Date): number[] | null {
  if (periodEnd.getUTCFullYear() !== periodStart.getUTCFullYear()) {
    return null; // cross-year window — not supported
  }

  const startMonth = periodStart.getUTCMonth();
  const endMonth   = periodEnd.getUTCMonth();
  const months: number[] = [];
  for (let m = startMonth; m <= endMonth; m++) {
    months.push(m);
  }
  return months;
}

/**
 * Returns true if every month in the assessment window is covered by ≥1 entry.
 *
 * Distinct behaviours:
 * - Empty entries + known window        → false (nothing covers any month)
 * - Any entries + unknown window (null) → true (vacuous pass — can't enforce)
 * - Empty entries + unknown window      → true (vacuous pass)
 *
 * Entries with period === null are ignored (contribute no coverage).
 */
export function hasCoverage(
  entries: Array<{ period: PeriodValue | null }>,
  periodStart: Date,
  periodEnd: Date,
): boolean {
  const assessmentMonths = getAssessmentMonths(periodStart, periodEnd);
  if (assessmentMonths === null) return true; // vacuous pass for unsupported windows

  // Build the set of months covered by all entries
  const covered = new Set<number>();
  for (const entry of entries) {
    for (const m of periodToMonths(entry.period)) {
      covered.add(m);
    }
  }

  // Every assessment month must be in the covered set
  return assessmentMonths.every((m) => covered.has(m));
}
